import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Card, Col, Container, Row, Badge, Button, Spinner, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useManagerSubscription } from '../../../../context/ManagerSubscriptionContext';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import { store } from '../../../../store/store';
import TabellaLista from '../../../components/Tabella/TabellaLista';
import { ColumnFilter } from '../../../components/table/FilteringTable/ColumnFilter';
import { segnalaMancanti } from '../../../components/Util/campiMancanti';

const GestioneAbbonamento = () => {
    const navigate = useNavigate();
    const { subscription, fetchStatus } = useManagerSubscription();
    const [transactions, setTransactions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [renewalLoading, setRenewalLoading] = useState(false);
    const [renewalError, setRenewalError] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);

    const [billingForm, setBillingForm] = useState({
        denomination: '',
        vatNumber: '',
        taxCode: '',
        sdiCode: '',
        pec: '',
        address: '',
        zipCode: '',
        city: '',
        province: '',
        nation: 'IT',
    });
    const [billingSaving, setBillingSaving] = useState(false);
    const [billingMessage, setBillingMessage] = useState(null);

    useEffect(() => {
        const b = subscription?.billingData;
        if (b) {
            setBillingForm({
                denomination: b.denomination || '',
                vatNumber: b.vatNumber || '',
                taxCode: b.taxCode || '',
                sdiCode: b.sdiCode || '',
                pec: b.pec || '',
                address: b.address || '',
                zipCode: b.zipCode || '',
                city: b.city || '',
                province: b.province || '',
                nation: b.nation || 'IT',
            });
        }
    }, [subscription?.billingData]);

    const handleBillingChange = (e) => {
        const { name, value } = e.target;
        setBillingForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveBillingData = async (e) => {
        e.preventDefault();
        setBillingMessage(null);

        // Gli obbligatori sono quelli che chiede il server per una struttura, che
        // e` sempre `business`: denominazione e partita IVA. Codice fiscale, codice
        // SDI e PEC sono facoltativi - nell'XML il codice fiscale compare solo se
        // c'e', e il recapito ripiega su "0000000" quando mancano sia SDI sia PEC.
        // Prima il form chiedeva l'opposto: PEC e codice fiscale obbligatori,
        // partita IVA no, e chi salvava senza partita IVA si vedeva tornare
        // l'errore del server in inglese.
        const mancanti = [];
        if (!billingForm.denomination.trim()) mancanti.push({ campo: 'fatt-denomination', messaggio: 'Manca la denominazione' });
        if (!billingForm.vatNumber.trim()) {
            mancanti.push({ campo: 'fatt-vatNumber', messaggio: 'Manca la partita IVA' });
        } else if (billingForm.vatNumber.trim().toUpperCase().startsWith('IT')) {
            mancanti.push({ campo: 'fatt-vatNumber', messaggio: 'La Partita IVA non deve iniziare con "IT": inserisci solo le 11 cifre numeriche' });
        }
        // L'indirizzo il server non lo controlla, ma senza finisce vuoto nella
        // fattura e lo SDI la scarta: tanto vale dirlo qui.
        if (!billingForm.address.trim()) mancanti.push({ campo: 'fatt-address', messaggio: "Manca l'indirizzo" });
        if (!billingForm.zipCode.trim()) mancanti.push({ campo: 'fatt-zipCode', messaggio: 'Manca il CAP' });
        if (!billingForm.city.trim()) mancanti.push({ campo: 'fatt-city', messaggio: 'Manca la città' });
        if (!billingForm.province.trim()) mancanti.push({ campo: 'fatt-province', messaggio: 'Manca la sigla della provincia' });

        // Codice SDI e PEC sono i due soli modi in cui la fattura puo` arrivare
        // alla struttura: senza nessuno dei due parte lo stesso, con destinatario
        // "0000000", ma viene depositata solo nel cassetto fiscale e nessuno se ne
        // accorge finche` non la reclama.
        //
        // La condizione e` la stessa che usa il generatore dell'XML, e va ripetuta
        // uguale: `0000000` e` il segnaposto "destinatario non noto", quindi e` un
        // campo compilato che non recapita niente - ed e` proprio cio` che il
        // suggerimento accanto al campo invita a scrivere. Anche un codice di
        // lunghezza sbagliata non vale.
        const sdiIndicato = (billingForm.sdiCode || '').trim().toUpperCase();
        const sdiRecapita = !!sdiIndicato && sdiIndicato !== '0000000' && /^[A-Z0-9]{6,7}$/.test(sdiIndicato);
        if (!sdiRecapita && !billingForm.pec.trim()) {
            mancanti.push({ campo: 'fatt-sdiCode', messaggio: "Indica il codice SDI oppure la PEC: servono a recapitarti la fattura (senza, la troverai solo nel cassetto fiscale dell'Agenzia delle Entrate)" });
        }
        if (segnalaMancanti(mancanti) > 0) return;

        setBillingSaving(true);
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            // `type` è obbligatorio a schema (business|consumer): la struttura è sempre business
            const res = await apiClient.updateBillingData(token, { ...billingForm, nation: 'IT', type: 'business' });
            if (res === true) {
                setBillingMessage({ type: 'success', text: 'Dati di fatturazione salvati con successo.' });
                await fetchStatus();
            } else {
                setBillingMessage({ type: 'danger', text: res.error || 'Errore durante il salvataggio.' });
            }
        } catch (err) {
            setBillingMessage({ type: 'danger', text: 'Errore di connessione.' });
        } finally {
            setBillingSaving(false);
        }
    };

    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                const token = store.getState().auth.auth.idToken;
                const apiClient = new ManagerSubscriptionsApiClient();
                const res = await apiClient.getTransactions(token);
                if (!res.error) {
                    setTransactions(res);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, []);

    // «Download XML» e «Download PDF» stanno nella scheda della transazione (03/10/2026)
    const apriTransazione = (row) => navigate('/struttura/strumenti/abbonamento-info/transazione/' + row.id, { state: { transazione: row } });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active': return <><i className="fa fa-circle me-1 text-success"></i> Attivo</>;
            case 'expired': return <><i className="fa fa-circle me-1 text-danger"></i> Scaduto</>;
            case 'free': return <><i className="fa fa-circle me-1 text-info"></i> Gratuito</>;
            case 'inactive': return <><i className="fa fa-circle me-1 text-secondary"></i> Inattivo</>;
            case 'refunded': return <><i className="fa fa-circle me-1 text-warning"></i> Rimborsato</>;
            default: return <><i className="fa fa-circle me-1 text-secondary"></i> {status}</>;
        }
    };

    const getInvoiceBadge = (status) => {
        switch (status) {
            case 'SENT': return <><i className="fa fa-circle me-1 text-primary"></i> Inviata</>;
            case 'ACCEPTED': return <><i className="fa fa-circle me-1 text-success"></i> Accettata</>;
            case 'DELIVERED': return <><i className="fa fa-circle me-1 text-success"></i> Consegnata</>;
            case 'REJECTED': return <><i className="fa fa-circle me-1 text-danger"></i> Scartata SDI</>;
            case 'NOT_SENT': return <><i className="fa fa-circle me-1 text-secondary"></i> Non Inviata</>;
            default: return <><i className="fa fa-circle me-1 text-secondary"></i> {status}</>;
        }
    };

    const handleCancelRenewal = async () => {
        setRenewalLoading(true);
        setRenewalError('');
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            const res = await apiClient.cancelRenewal(token);
            if (res === true) {
                setShowCancelModal(false);
                await fetchStatus();
            } else {
                setRenewalError(res.error || 'Errore durante la cancellazione del rinnovo');
            }
        } catch (e) {
            setRenewalError('Errore di connessione');
        } finally {
            setRenewalLoading(false);
        }
    };

    const handleReactivateRenewal = async () => {
        setRenewalLoading(true);
        setRenewalError('');
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            const res = await apiClient.reactivateRenewal(token);
            if (res === true) {
                await fetchStatus();
            } else {
                setRenewalError(res.error || 'Errore durante la riattivazione del rinnovo');
            }
        } catch (e) {
            setRenewalError('Errore di connessione');
        } finally {
            setRenewalLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('it-IT');
    };

    const data_column = useMemo(() => [
        {
            Header: 'Data',
            accessor: 'createdAt',
            Filter: ColumnFilter,
            Cell: ({ row }) => formatDate(row.original.createdAt || row.original.startDate)
        },
        {
            Header: 'Transazione',
            accessor: 'transactionId',
            Filter: ColumnFilter,
            Cell: ({ value }) => value || '-'
        },
        {
            Header: 'Importo',
            accessor: 'priceEur',
            disableFilters: true,
            Cell: ({ value }) => value ? `€ ${Number(value).toFixed(2)}` : 'Gratuito'
        },
        {
            Header: 'Stato Fattura',
            accessor: 'invoiceStatus',
            disableFilters: true,
            Cell: ({ value }) => value ? getInvoiceBadge(value) : '-'
        },
        {
            Header: 'Stato Abbonamento',
            accessor: 'status',
            Filter: ColumnFilter,
            Cell: ({ value }) => getStatusBadge(value)
        },
    ], []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Container fluid>
            <div className="row page-titles mx-0">
                <div className="col-sm-6 p-md-0">
                    <div className="welcome-text">
                        <h4>Gestione Abbonamento</h4>
                    </div>
                </div>
            </div>

            <Row>
                <Col xl={12}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Il tuo abbonamento</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {subscription ? (
                                <>
                                    {(() => {
                                        const isTrial = subscription.status === 'active' && subscription.priceEur === 0;
                                        return (
                                    <>
                                    <Row className="mb-3">
                                        <Col sm={3} className="mb-3">
                                            <h6 className="text-muted">Stato</h6>
                                            <div className="fs-18">{isTrial ? <Badge bg="info">Prova gratuita</Badge> : getStatusBadge(subscription.status)}</div>
                                        </Col>
                                        <Col sm={3} className="mb-3">
                                            <h6 className="text-muted">Prezzo annuale</h6>
                                            <div className="fs-18">{isTrial ? 'Gratuita (prova 7gg)' : (subscription.priceEur ? `€ ${Number(subscription.priceEur).toFixed(2)}` : 'Gratuito')}</div>
                                        </Col>
                                        <Col sm={3} className="mb-3">
                                            <h6 className="text-muted">
                                                {isTrial
                                                    ? 'Fine prova gratuita'
                                                    : subscription.cancelAtPeriodEnd
                                                        ? 'Scadenza (rinnovo annullato)'
                                                        : subscription.status === 'active'
                                                            ? 'Prossimo rinnovo automatico'
                                                            : 'Data Scadenza'}
                                            </h6>
                                            <div className="fs-18 font-w600">{formatDate(subscription.endDate)}</div>
                                        </Col>
                                        {!isTrial && subscription.status === 'active' && (
                                            <Col sm={3} className="mb-3">
                                                <h6 className="text-muted">Rinnovo automatico</h6>
                                                <div>
                                                    {subscription.cancelAtPeriodEnd
                                                        ? <Badge bg="warning" text="dark">Annullato</Badge>
                                                        : <Badge bg="success">Attivo</Badge>
                                                    }
                                                </div>
                                            </Col>
                                        )}
                                    </Row>

                                    {isTrial && (
                                        <Alert variant="info" className="mb-3">
                                            Sei in prova gratuita. Al termine dei 7 giorni l'accesso al gestionale verrà sospeso finché non sottoscriverai l'abbonamento.
                                            <div className="mt-2">
                                                <Button variant="primary" size="sm" onClick={() => navigate('/abbonamento')}>Attiva l'abbonamento ora</Button>
                                            </div>
                                        </Alert>
                                    )}

                                    {!isTrial && subscription.status === 'active' && subscription.cancelAtPeriodEnd && (
                                        <Alert variant="warning" className="mb-3">
                                            Il rinnovo automatico è stato annullato. Il tuo accesso resterà attivo fino al <strong>{formatDate(subscription.endDate)}</strong>, dopodiché l'abbonamento scadrà senza ulteriori addebiti.
                                        </Alert>
                                    )}

                                    {renewalError && <Alert variant="danger">{renewalError}</Alert>}

                                    {!isTrial && subscription.status === 'active' && (
                                        <div className="mt-2">
                                            {subscription.cancelAtPeriodEnd ? (
                                                <Button variant="success" size="sm" onClick={handleReactivateRenewal} disabled={renewalLoading}>
                                                    {renewalLoading ? <Spinner size="sm" animation="border" /> : 'Riattiva rinnovo automatico'}
                                                </Button>
                                            ) : (
                                                <Button variant="outline-danger" size="sm" onClick={() => setShowCancelModal(true)} disabled={renewalLoading}>
                                                    Annulla rinnovo automatico
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                    </>
                                        );
                                    })()}
                                </>
                            ) : (
                                <p>Caricamento stato abbonamento...</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xl={12}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Dati di fatturazione</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {billingMessage && <Alert variant={billingMessage.type}>{billingMessage.text}</Alert>}
                            <Form onSubmit={handleSaveBillingData} noValidate>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-denomination">Denominazione *</Form.Label>
                                            <Form.Control id="fatt-denomination" required type="text" name="denomination" value={billingForm.denomination} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-vatNumber">Partita IVA *</Form.Label>
                                            <Form.Control id="fatt-vatNumber" required type="text" name="vatNumber" value={billingForm.vatNumber} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-taxCode">Codice Fiscale</Form.Label>
                                            <Form.Control id="fatt-taxCode" type="text" name="taxCode" value={billingForm.taxCode} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-sdiCode">
                                                Codice SDI{' '}
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Se non hai un codice SDI scrivi 0000000 e compila la PEC: la fattura ti arrivera` li`.</Tooltip>}>
                                                    <i className="fa fa-info-circle text-muted" style={{ cursor: 'pointer' }} />
                                                </OverlayTrigger>
                                            </Form.Label>
                                            <Form.Control id="fatt-sdiCode" type="text" name="sdiCode" value={billingForm.sdiCode} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-pec">PEC</Form.Label>
                                            <Form.Control id="fatt-pec" type="email" name="pec" value={billingForm.pec} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-address">Indirizzo *</Form.Label>
                                            <Form.Control id="fatt-address" required type="text" name="address" value={billingForm.address} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-zipCode">CAP *</Form.Label>
                                            <Form.Control id="fatt-zipCode" required type="text" name="zipCode" value={billingForm.zipCode} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-city">Città *</Form.Label>
                                            <Form.Control id="fatt-city" required type="text" name="city" value={billingForm.city} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="fatt-province">Provincia (Sigla) *</Form.Label>
                                            <Form.Control id="fatt-province" required type="text" maxLength={2} name="province" value={billingForm.province} onChange={handleBillingChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Nazione</Form.Label>
                                            <Form.Control type="text" value="Italia" readOnly disabled />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" type="submit" disabled={billingSaving}>
                                        {billingSaving ? <Spinner size="sm" animation="border" /> : 'Salva dati di fatturazione'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {!loading && (
                <TabellaLista
                    cardTitle="Storico Transazioni e Fatture"
                    hasSearch={true}
                    hasFilter={false}
                    data_column={data_column}
                    data_json={transactions}
                    openColumn="createdAt"
                    openRow={(row) => apriTransazione(row)}
                    sort_by="createdAt"
                    sort_type="desc"
                />
            )}

            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Annullare il rinnovo automatico?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Il tuo abbonamento resterà attivo fino al <strong>{formatDate(subscription?.endDate)}</strong>. Dopo quella data non verrà effettuato alcun addebito e perderai l'accesso alla piattaforma.
                    </p>
                    <p className="mt-2 mb-0">
                        Potrai riattivare il rinnovo automatico in qualsiasi momento prima della scadenza.
                    </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center sid-footer-conferma">
                    <Button className="btn btn-light btn-add" onClick={() => setShowCancelModal(false)} disabled={renewalLoading}>
                        Annulla
                    </Button>
                    <Button className="btn btn-danger btn-add" onClick={handleCancelRenewal} disabled={renewalLoading}>
                        {renewalLoading ? <Spinner size="sm" animation="border" /> : 'Conferma annullamento'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default GestioneAbbonamento;
